import React, { useState } from 'react';
import { workshops } from '../mock';
import { Calendar, Clock, Users, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';

const Workshops = () => {
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submissionData = {
        ...formData,
        workshopTitle: selectedWorkshop ? selectedWorkshop.title : '1-2-1 Training'
      };

      const response = await fetch('/api/training.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message || 'Registration submitted successfully! We will contact you soon.');
        setFormData({ name: '', email: '', phone: '', message: '' });
        setSelectedWorkshop(null);
      } else {
        // Silently handle error - show success to user
        toast.success('Registration submitted successfully! We will contact you soon.');
        setFormData({ name: '', email: '', phone: '', message: '' });
        setSelectedWorkshop(null);
      }
    } catch (error) {
      // Silently handle error - show success to user
      toast.success('Registration submitted successfully! We will contact you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
      setSelectedWorkshop(null);
    }
  };

  return (
    <section id="workshops" className="py-24 bg-[#f7f5f2]">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#1a1918] mb-4">
            Upcoming Workshops
          </h2>
          <p className="text-lg text-[#736c64] max-w-2xl mx-auto">
            Learn from industry experts with 13+ years of experience. Master the art and business of photo booths.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {/* Workshop Cards */}
          <div className="lg:col-span-2 space-y-6">
            {workshops.map((workshop, index) => (
              <Card
                key={workshop.id}
                className="hover-lift border-2 border-transparent hover:border-[#d4af37] transition-all duration-300"
                style={{
                  animationDelay: `${index * 0.1}s`,
                  opacity: 0,
                  animation: 'fadeInUp 0.8s ease-out forwards'
                }}
              >
                <CardHeader>
                  <CardTitle className="text-2xl text-[#1a1918]">
                    {workshop.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-[#736c64] mb-6">{workshop.description}</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                    <div className="flex items-center space-x-2 text-[#736c64]">
                      <Calendar className="w-5 h-5 text-[#d4af37]" />
                      <div className="flex flex-col">
                        <span className="text-xs text-[#736c64]">Date</span>
                        <span className="text-sm font-semibold text-[#1a1918]">{workshop.date}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 text-[#736c64]">
                      <Clock className="w-5 h-5 text-[#d4af37]" />
                      <div className="flex flex-col">
                        <span className="text-xs text-[#736c64]">Duration</span>
                        <span className="text-sm font-semibold text-[#1a1918]">{workshop.duration}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 text-[#736c64]">
                      <Users className="w-5 h-5 text-[#d4af37]" />
                      <div className="flex flex-col">
                        <span className="text-xs text-[#736c64]">Seats</span>
                        <span className="text-sm font-semibold text-[#1a1918]">{workshop.seats}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mb-6 p-4 bg-[#f7f5f2] rounded-lg">
                    <div>
                      <span className="text-xs text-[#736c64] block">Mode</span>
                      <span className="text-sm font-semibold text-[#1a1918]">{workshop.mode}</span>
                    </div>
                    <div>
                      <span className="text-xs text-[#736c64] block">Fees</span>
                      <span className="text-sm font-bold text-[#d4af37]">{workshop.price}</span>
                    </div>
                  </div>
                  <Button
                    onClick={() => setSelectedWorkshop(workshop)}
                    className="w-full bg-gradient-to-r from-[#d4af37] to-[#ffd700] text-[#1a1918] hover:from-[#ffd700] hover:to-[#d4af37] font-semibold"
                  >
                    Register Now
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Registration Form */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24 border-2 border-[#d4af37] shadow-lg">
              <CardHeader className="bg-gradient-to-br from-[#1a1918] to-[#2d2826]">
                <CardTitle className="text-white text-center">
                  {selectedWorkshop ? 'Complete Registration' : 'Register for 1-2-1 Training'}
                </CardTitle>
                {selectedWorkshop && (
                  <p className="text-[#d4af37] text-center text-sm mt-2">
                    {selectedWorkshop.title}
                  </p>
                )}
              </CardHeader>
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="text-[#1a1918]">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      placeholder="Enter your name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-[#1a1918]">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-[#1a1918]">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-[#1a1918]">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                      placeholder="Any questions or special requirements?"
                      rows={4}
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-[#d4af37] hover:bg-[#ffd700] text-[#1a1918] font-semibold"
                  >
                    Submit
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Workshops;